import { AddImagesResponse, DataVersion } from '@hakimo-ui/hakimo/types';
import { useHttpClient, withDataVersion } from '@hakimo-ui/hakimo/util';
import { useMutation, useQueryClient } from 'react-query';

export interface UnifiedAddImagesParams {
  productType?: string;
  alarmGroupId?: number | string;
  location_alarm_id?: number | string;
  images: File[];
  comment: string;
  timestamp?: number;
}

export function useUnifiedAddImages(
  onSuccess?: (data: AddImagesResponse) => void,
  onError?: (err: Error) => void,
  dataVersion?: DataVersion
) {
  const queryClient = useQueryClient();
  const httpClient = useHttpClient();

  return useMutation<AddImagesResponse, Error, UnifiedAddImagesParams>({
    mutationFn: async (variables: UnifiedAddImagesParams) => {
      const url = '/v2/orm/escalations/add_comment_with_snapshots';

      const formData = new FormData();

      formData.append('comment', variables.comment);
      if (variables.timestamp != null) {
        formData.append('timestamp', String(variables.timestamp));
      }

      if (variables.productType === 'scan') {
        formData.append('productType', 'scan');
        formData.append('alarmGroupId', String(variables.alarmGroupId));
      } else {
        formData.append('productType', 'remote-guarding');
        formData.append('locationAlarmId', String(variables.location_alarm_id));
      }

      variables.images.forEach((image) => {
        formData.append('snapshots', image);
      });

      const request = new Request(url, {
        method: 'POST',
        body: formData,
      });
      withDataVersion(request, dataVersion);

      return httpClient<AddImagesResponse>(request, {
        responseModifier: async (response: Response) => {
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return await response.json();
            } else {
              return { s3_urls: [], comment_id: '' } as AddImagesResponse;
            }
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        },
      });
    },
    onSuccess: (data: AddImagesResponse, variables: UnifiedAddImagesParams) => {
      if (variables.productType === 'scan') {
        queryClient.invalidateQueries(['scanIncidentImages']);
        if (variables.alarmGroupId) {
          queryClient.invalidateQueries([
            'location_alarm_updates',
            String(variables.alarmGroupId),
          ]);
        }
      } else {
        queryClient.invalidateQueries(['incidentImages']);
        if (variables.location_alarm_id) {
          queryClient.invalidateQueries([
            'location_alarm_updates',
            String(variables.location_alarm_id),
          ]);
        }
      }
      onSuccess?.(data);
    },
    onError,
  });
}

export default useUnifiedAddImages;
